import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Heart, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/favorites`);
      setFavorites(response.data);
    } catch (error) {
      logger.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const removeFavorite = async (propertyId) => {
    try {
      await axios.delete(`${API_URL}/api/favorites/${propertyId}`);
      setFavorites(favorites.filter(p => p.id !== propertyId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="favorites-page">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-2 block uppercase font-medium">Collection</span>
          <h1 className="font-['Playfair_Display'] text-4xl text-[#1A1A1A]">My Favorites</h1>
          <p className="text-slate-500 mt-2">{favorites.length} saved {favorites.length === 1 ? 'property' : 'properties'}</p>
        </div>

        {favorites.length > 0 ? (
          <div className="property-grid">
            {favorites.map((property) => (
              <div key={property.id} className="relative">
                <PropertyCard property={property} />
                <button
                  onClick={() => removeFavorite(property.id)}
                  className="absolute top-4 right-4 z-10 w-10 h-10 bg-red-500 text-white flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                  data-testid={`remove-fav-${property.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="luxury-card p-16 text-center">
            <Heart className="h-16 w-16 text-slate-200 mx-auto mb-4" />
            <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-3">No Favorites Yet</h3>
            <p className="text-slate-500 mb-8">Explore our collection and save properties you love</p>
            <Link to="/properties">
              <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-wider" data-testid="explore-btn">
                EXPLORE PROPERTIES
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
